import {
  GetShippingConfigurationRequestQueryParamsType,
  GetShippingConfigurationResponseBodySchema,
  GetShippingConfigurationResponseBodyType,
} from "@intelligems/ig-types";
import { QueryClient, useQuery } from "@tanstack/react-query";
import { api } from "@src/api/api";
import { shippingTransitionRefetchInterval } from "./transition";

export const shippingConfigurationKey = ["shipping-configuration"] as const;

export const shippingConfigurationQueryOptions = {
  staleTime: 30_000,
  refetchOnWindowFocus: false,
} as const;

export const getShippingConfiguration = async (
  params: Partial<GetShippingConfigurationRequestQueryParamsType> = {}
): Promise<GetShippingConfigurationResponseBodyType> => {
  const response = await api.get<GetShippingConfigurationResponseBodyType>(
    "/v2/shipping-configuration",
    { params }
  );
  return GetShippingConfigurationResponseBodySchema.parse(response.data);
};

export const useShippingConfiguration = (
  params: Partial<GetShippingConfigurationRequestQueryParamsType> = {},
  enabled = true
) =>
  useQuery({
    queryKey: [...shippingConfigurationKey, params],
    queryFn: () => getShippingConfiguration(params),
    enabled,
    ...shippingConfigurationQueryOptions,
    // The merchant changes the shipping model in another tab after a
    // preparation finishes. Refresh this read when they return.
    refetchOnWindowFocus: "always",
    // Polls only while a model transition is queued or running; the
    // websocket push still invalidates on every change.
    refetchInterval: (query) =>
      shippingTransitionRefetchInterval(query.state.data),
  });

/**
 * Yields each page of the org-wide configuration, guarding against a cursor
 * that does not advance. The first page is the base configuration read, so it
 * comes from (and fills) that query's cache instead of a second request.
 */
export async function* shippingConfigurationPages(
  client: QueryClient
): AsyncGenerator<GetShippingConfigurationResponseBodyType> {
  const seen = new Set<string>();
  let response = await client.fetchQuery({
    queryKey: [...shippingConfigurationKey, {}],
    queryFn: () => getShippingConfiguration({}),
    ...shippingConfigurationQueryOptions,
  });
  while (true) {
    yield response;
    if (!response.pageInfo.hasNextPage) {
      return;
    }
    const after = response.pageInfo.endCursor;

    if (!after || seen.has(after)) {
      throw new Error(
        "Shipping configuration pagination did not advance. Refresh to try again."
      );
    }
    seen.add(after);
    response = await getShippingConfiguration({ after });
  }
}

export const getShippingMarketSummaries = async (client: QueryClient) => {
  const markets: GetShippingConfigurationResponseBodyType["markets"] = [];
  for await (const response of shippingConfigurationPages(client)) {
    markets.push(...response.markets);
    if (
      response.capabilities.shippingModel !== "markets" ||
      !response.capabilities.canRead
    ) {
      break;
    }
  }
  return markets;
};

export const useShippingMarketSummaries = (enabled: boolean) =>
  useQuery({
    queryKey: [...shippingConfigurationKey, "markets"],
    queryFn: ({ client }) => getShippingMarketSummaries(client),
    enabled,
  });
